import { OpenInNew } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import CLocalizedText from '../../components/CLocalizedText';
import CPageModule from '../../components/CPageModule';
import CPageModulePlot from '../../components/CPageModulePlot';
import CPandasTable from '../../components/CPandasTable';
import CResponsiveGrid from '../../components/CResponsiveGrid';
import CStatefulContainer from '../../components/CStatefulContainer';
import CTextFieldDisplay from '../../components/CTextFieldDisplay';
import Page from '../../components/layout/Page';
import { BreakpointsInPx } from '../../enums/BreakpointsInPx';
import { updateStateAttribute } from '../../helpers/helper';
import { useAppSelector } from '../../hooks/hooks';
import type { IResponse } from '../../models/IResponse';
import { type PlotlyResponse } from '../../models/PlotlyModel';
import { type TPandasTable } from '../../models/TPandasTable';
import PublicFactsheetServices from '../../services/publicFactsheetServices';
import CAnteilsklasseTab from './CAnteilsklasseTab';
import FactsheetStickyHeader from './FactsheetStickyHeader';

interface State {
  overview: IResponse<PlotlyResponse>;
  overviewTable: IResponse<TPandasTable>;
  targetScrollProgress?: number;
}

export default function PFactsheetIndex() {
  const { locale, dashboardOutletDimensions } = useAppSelector((state) => state.dashboard);
  const { factsheetId } = useParams();
  const [state, setState] = useState<State>({
    overview: { state: { isLoading: true, isError: false }, data: undefined },
    overviewTable: { state: { isLoading: true, isError: false }, data: undefined }
  });

  const fetchCharts = async () => {
    updateStateAttribute<State>(
      {
        overview: {
          ...state.overview,
          state: { ...state.overview.state, isLoading: true }
        }
      },
      setState
    );

    const res = await PublicFactsheetServices.getFactsheetChart(factsheetId ?? '');

    if (res?.wasSuccessful()) {
      updateStateAttribute<State>(
        {
          overview: {
            ...state.overview,
            state: { isLoading: false, isError: false },
            data: res.getData()
          }
        },
        setState
      );
    } else {
      updateStateAttribute<State>(
        {
          overview: {
            ...state.overview,
            state: {
              isLoading: false,
              isError: true,
              strError: res.getErrorString?.(),
              objError: res.getErrorObj?.()
            }
          }
        },
        setState
      );
    }
  };

  const fetchTable = async () => {
    updateStateAttribute<State>(
      {
        overviewTable: {
          ...state.overviewTable,
          state: { ...state.overviewTable.state, isLoading: true }
        }
      },
      setState
    );

    const res = await PublicFactsheetServices.getFactsheetOverviewTable(factsheetId ?? '');

    if (res.wasSuccessful()) {
      updateStateAttribute<State>(
        {
          overviewTable: {
            ...state.overviewTable,
            state: { isLoading: false, isError: false },
            data: res.getData()
          }
        },
        setState
      );
    } else {
      updateStateAttribute<State>(
        {
          overviewTable: {
            ...state.overviewTable,
            state: {
              isLoading: false,
              isError: true,
              strError: res.getErrorString?.(),
              objError: res.getErrorObj?.()
            }
          }
        },
        setState
      );
    }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([fetchTable(), fetchCharts()]);
    })();
  }, [locale]);

  return (
    <Page stickyHeader={<FactsheetStickyHeader />} targetScrollProgress={state.targetScrollProgress} padStickyHeader>
      <CPageModule fullWidth>
        <CPageModule submodule>
          <h2>
            <CLocalizedText dictKey={'fsovTitle'} />
          </h2>
          <CLocalizedText dictKey={'fsovDesc'} collapsible />
        </CPageModule>
        <CPageModulePlot
          data={state?.overview?.data?.data}
          layout={state?.overview?.data?.layout}
          apiRequestState={state?.overview.state}
          showWhen={!!state?.overview.data}
          aspectRatio={4}
        />
        <CPageModule submodule>
          <CResponsiveGrid maxColumns={2} style={{ columnGap: '1.5rem', rowGap: '1.5rem' }}>
            <div className={'flex items-center'}>
              <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
                <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovAllocH1'} />}>
                  <CLocalizedText dictKey={'fsovAllocB1'} />
                </CTextFieldDisplay>
                <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovAllocH2'} />}>
                  <CLocalizedText dictKey={'fsovAllocB2'} />
                </CTextFieldDisplay>
                <CTextFieldDisplay label={<CLocalizedText dictKey={'fsovAllocH3'} />}>
                  <CLocalizedText dictKey={'fsovAllocB3'} />
                </CTextFieldDisplay>
              </CPageModule>
            </div>
            <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
              {!!state?.overview?.data && (
                <CStatefulContainer
                  apiRequestState={state?.overviewTable.state}
                  showContent={!!state?.overviewTable.data}
                >
                  <CPandasTable
                    data={state?.overviewTable?.data}
                    style={{ minWidth: 'auto', width: '100%' }}
                    colSpanArr={[1, 1, 1]}
                    colAlignmentArr={[undefined, 'right']}
                    showIndex
                  />
                </CStatefulContainer>
              )}
            </CPageModule>
          </CResponsiveGrid>
        </CPageModule>
      </CPageModule>
      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CPageModule submodule>
          <h3>
            <CLocalizedText dictKey={'fsovP3H1'} />
          </h3>
        </CPageModule>
        <CAnteilsklasseTab />
      </CPageModule>
      <CPageModule>
        <CResponsiveGrid maxColumns={3} style={{ rowGap: '1.5rem', columnGap: '1.5rem' }}>
          <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
            <h3>
              <CLocalizedText dictKey={'fsovP8H1'} />
            </h3>
            <div>
              <CLocalizedText dictKey={'fsovP8Desc'} />
              <Link to={'https://amfileon.com/'} target={'_blank'}>
                https://amfileon.com <OpenInNew />
              </Link>
            </div>
          </CPageModule>
          <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
            <h3>
              <CLocalizedText dictKey={'fsovP6H1'} />
            </h3>
            <div>
              <CLocalizedText dictKey={'fsovP6Desc'} />
              <Link to={'https://bnpparibas.com'} target={'_blank'}>
                https://bnpparibas.com <OpenInNew />
              </Link>
            </div>
          </CPageModule>
          <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
            <h3>
              <CLocalizedText dictKey={'fsovP7H1'} />
            </h3>
            <div>
              <CLocalizedText dictKey={'fsovP7Desc'} />
              <Link to={'https://universal-investment.com/'} target={'_blank'}>
                https://universal-investment.com <OpenInNew />
              </Link>
            </div>
          </CPageModule>
        </CResponsiveGrid>
        <CResponsiveGrid maxColumns={3} style={{ rowGap: '1.5rem', columnGap: '1.5rem' }}>
          <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
            <h3>
              <CLocalizedText dictKey={'fsovP4H1'} />
            </h3>
            <CLocalizedText dictKey={'fsovP4Desc'} />
          </CPageModule>
          <div
            style={{
              gridColumn:
                dashboardOutletDimensions.w < BreakpointsInPx.BREAKPOINT_3 ? 'span 1 / span 1' : 'span 2 / span 2'
            }}
          >
            <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
              <h3>
                <CLocalizedText dictKey={'fsovP5H1'} />
              </h3>
              <CLocalizedText dictKey={'fsovP5Desc'} />
            </CPageModule>
          </div>
        </CResponsiveGrid>
      </CPageModule>
      <CPageModule style={{ paddingTop: 0 }}>
        <h3>
          <CLocalizedText dictKey={'fsovP9H1'} />
        </h3>
        <CLocalizedText dictKey={'fsovP9Desc'} />
      </CPageModule>
    </Page>
  );
}
